/**
 * uPlot docs: https://github.com/leeoniya/uPlot/tree/master/docs */
import { type ElementRef, type OnDestroy, untracked } from '@angular/core';
import { computed, Directive, effect, inject, input, viewChild } from '@angular/core';
import uPlot from 'uplot';
import type { RawClientDataDTO } from './model/raw-client-data.dto';
import type { MindUplotOptions } from './model/mind-uplot-options';
import { TranslocoService } from '@jsverse/transloco';
import type { RawClientDataType } from './model/raw-client-data-type';
import type { MindUplotComponentOptions } from './model/mind-uplot-component-options';
import { isNil } from '@pct/ts-typeguard';

@Directive()
export abstract class MindUplotComponent implements OnDestroy {
  container = viewChild<ElementRef>('chartContainer');

  readonly options = input<Partial<MindUplotOptions>>(); // a chart testreszabásához
  readonly componentOptions = input.required<MindUplotComponentOptions>();

  protected uPlotInstance?: uPlot;
  protected readonly xData: number[] = [];
  // Minden típushoz egy párhuzamos tömb, amelyik undefined-ot tartalmaz, ha nincs adat az adott időponthoz
  protected seriesByType: Partial<Record<RawClientDataType, (number | undefined)[]>> = {};
  // Gyors kereséshez: melyik index tartozik melyik időponthoz
  protected readonly xIndexMap = new Map<number, number>();
  protected data: uPlot.AlignedData = [this.xData];
  protected readonly translocoService = inject(TranslocoService);

  protected latestDelta = 0; //nem garantált, hogy az utolsó kapott delta a legkésőbbi, ezért eltárolom
  protected timer: number | undefined;

  protected constructor() {
    effect(onCleanup => {
      const container = this.container();
      if (container === undefined) {
        return;
      }
      // A meglévő pontok hosszára igazítjuk a sorozat-tömböket, hogy ne csússzanak el az xData-tól
      this.seriesByType = Object.fromEntries(
        untracked(this.componentOptions).dataTypes.map(type => [type, new Array(this.xData.length).fill(undefined)]),
      );
      this.data = [this.xData, ...Object.values(this.seriesByType)] as uPlot.AlignedData;

      const defaults = this.getDefaultOptions(container);

      // Az átadott opciókat összefésüljük az alapértelmezettekkel
      // Az input opcióik felülírják az alapértelmezetteket
      const opts: uPlot.Options = {
        ...defaults,
        ...this.options(),
      };

      this.uPlotInstance = new uPlot(opts, this.data, container.nativeElement);

      onCleanup(() => {
        this.uPlotInstance?.destroy();
      });
    });

    const deviceId = computed(() => this.componentOptions().deviceId);
    // Új session kezdete — resetelni az összes adatot
    effect(() => {
      deviceId();
      this.resetForNewSession();
    });
  }

  protected abstract getDefaultOptions(container: ElementRef): uPlot.Options;

  protected abstract resetForNewSession(): void;

  /**
   * Közös pontfelvitel: beírja az új pontokat az xData/seriesByType tömbökbe.
   * Az xData mindig növekvő sorrendben marad, visszatekert/párhuzamos betöltés esetén is.
   */
  protected addPointsToSeries(points: RawClientDataDTO[]): void {
    const seriesList = Object.values(this.seriesByType);

    for (const point of points) {
      if (this.latestDelta < point.delta) {
        this.latestDelta = point.delta;
      }

      let idx = this.xIndexMap.get(point.delta);

      if (idx === undefined) {
        const lastX = this.xData.at(-1);

        if (lastX === undefined || point.delta > lastX) {
          // Gyors út: az új delta nagyobb a tömb utolsó eleménél → egyszerű push
          idx = this.xData.length;
          this.xData.push(point.delta);
          for (const series of seriesList) {
            series.push(undefined);
          }
          this.xIndexMap.set(point.delta, idx);
        } else {
          // Lassú út (visszatekert/párhuzamos betöltés): bináris kereséssel
          // megtaláljuk a helyes pozíciót, hogy xData növekvő maradjon
          let lo = 0;
          let hi = this.xData.length;
          while (lo < hi) {
            const mid = (lo + hi) >>> 1;
            if (this.xData[mid] < point.delta) lo = mid + 1;
            else hi = mid;
          }
          const insertAt = lo;

          this.xData.splice(insertAt, 0, point.delta);
          for (const series of seriesList) {
            series.splice(insertAt, 0, undefined);
          }

          // A splice eltolta az insertAt utáni összes elem indexét — frissítjük a map-et
          for (let i = insertAt; i < this.xData.length; i++) {
            this.xIndexMap.set(this.xData[i], i);
          }

          idx = insertAt;
        }
      }

      const targetSeries = this.seriesByType[point.type];
      if (targetSeries !== undefined) {
        targetSeries[idx] = point.value;
      }
    }
  }

  /**
   * Régi pontok levágása, hogy felszabaduljon a memória.
   */
  protected evictOldPoints(cutoff: number): void {
    const firstKeepIndex = this.xData.findIndex(x => x >= cutoff);
    const evictCount = firstKeepIndex === -1 ? this.xData.length : firstKeepIndex;

    if (evictCount === 0) {
      return;
    }

    this.xData.splice(0, evictCount);

    Object.values(this.seriesByType).forEach(series => {
      series?.splice(0, evictCount);
    });

    this.xIndexMap.clear();

    this.xData.forEach((x, index) => {
      this.xIndexMap.set(x, index);
    });
  }

  protected reset(): void {
    this.xData.length = 0;
    this.xIndexMap.clear();
    Object.values(this.seriesByType).forEach(series => {
      series.length = 0;
    });
    this.latestDelta = 0;
  }

  getInstance(): uPlot | undefined {
    return this.uPlotInstance;
  }

  ngOnDestroy(): void {
    if (!isNil(this.timer)) {
      cancelAnimationFrame(this.timer);
    }
    this.uPlotInstance?.destroy();
  }
}
